'use client';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { ResponseNativeCard } from '@/src/interfaces/data-contracts';
import { DeviceContext } from '@/src/store/contexts/device-context';
import NativeCardModal from './native-card-modal';
import NativeCardTooltip from '../../ui/tooltip/native-card-tooltip';
import { BlogModulesActions } from './actions';
import ImageWithFallback from '@/src/features/ui/image/image-with-fallback';
import { NCContainer } from './nc-container';
import { ProgrammableNativeCard } from './programmable-native-card';
import classNames from 'classnames';
import { RelLink } from '../../ui/rel-link';
import { ClipboardChecklist, GoldMedal, HandphoneLock } from '../../ui/icons/icons';
import Modal from '../../ui/modal/modal';

const getIntegrationTypeFromLink = (link: string | undefined): string | null => {
  if (!link) return null;

  if (link.startsWith('$dijikolay')) {
    return 'dijikolay';
  }
  return null;
};

type Props = {
  item: ResponseNativeCard;
};

type NativeCardContentProps = {
  item: ResponseNativeCard;
  handleOpenPopup: () => Promise<void>;
  handleClosePopup: () => void;
  imageElement: React.JSX.Element;
  isPopupOpen: boolean;
  hasIntegration: boolean;
  integrationType: string | null;
  rsParam: any;
  error: string | null;
  isLoadingIntegrationData: boolean;
  integrationData: any;
};

const ConditionsModal = ({
  isPopupOpen,
  handleClosePopup,
  item,
}: {
  item: ResponseNativeCard;
  isPopupOpen: boolean;
  handleClosePopup: () => void;
}) => {
  const { isMobile } = useContext(DeviceContext);

  return (
    <Modal
      className="md:max-w-auto h-auto max-w-[343px] px-4 pb-6 pt-5 md:min-w-[800px] md:px-8 md:pb-8 md:pt-8"
      show={isPopupOpen}
      onClose={handleClosePopup}
      data-testid="nativeCardModal"
    >
      {
        <>
          <div className="mt-4 flex items-start justify-between">
            <div className="flex min-w-fit flex-1 items-center gap-5">
              <figure className="relative h-[52px] w-[104px]" data-testid="bankLogo">
                {
                  //@ts-ignore
                  <ImageWithFallback src={item.bankLogoPath} alt={item.name} title={item.name} fill />
                }
              </figure>
              <div className="min-w-fit flex-1">
                {
                  //@ts-ignore
                  <h2 className="text-base text-black1000" data-testid="modalBankName">{item.bankName || ''}</h2>
                }
                {
                  //@ts-ignore
                  <p className="font-semibold text-black800" data-testid="modalProductName">
                    {item.bankName ? `${item.bankName} Müşterisi Ol` : ''}
                  </p>
                }
              </div>
            </div>
            {
              //@ts-ignore
              item.link && !isMobile && !item.isOfferPopUp && (
                <RelLink
                  href={item.link ?? ''}
                  target={item.target ?? '_blank'}
                  className={`flex ${isMobile ? 'flex-col justify-between' : 'justify-center'} ml-auto h-full w-fit items-center`}
                  data-testid="nativeCardLink"
                >
                  <div
                    className="ml-auto w-fit rounded-[5px] bg-[#f26722] px-5 py-3 text-[16px] font-semibold text-[#fff]"
                    data-testid="applyNowButton"
                  >
                    Hemen Başvur <span className="ml-1">→</span>
                  </div>
                </RelLink>
              )
            }
          </div>
          <div>
            {
              //@ts-ignore
              item.link && isMobile && !item.isOfferPopUp && (
                <div className="mt-6 w-fit">
                  <RelLink href={item.link ?? ''} target={item.target ?? '_blank'} data-testid="nativeCardLink">
                    <div
                      className="w-fit rounded-[5px] bg-[#f26722] px-5 py-3 text-[16px] font-semibold text-[#fff]"
                      data-testid="applyNowButton"
                    >
                      Hemen Başvur <span className="ml-1">→</span>
                    </div>
                  </RelLink>
                </div>
              )
            }
          </div>
          {
            //@ts-ignore
            item.isOfferPopUp && item.nativeCardPictures.qrImagePath && !isMobile && (
              <div data-testid="qr-info" className="mb-4 mt-6 flex">
                <div className="mr-[24px] flex h-[168px] flex-1 items-center rounded-[8px] border-[2px] border-[#d5dde3] p-4">
                  <figure className="relative mr-4 h-[128px] w-[128px] shrink-0" data-testid="qrImage">
                    {
                      <ImageWithFallback
                        //@ts-ignore
                        src={item.nativeCardPictures.qrImagePath}
                        alt={item.name}
                        title={item.name}
                        fill
                      />
                    }
                  </figure>
                  <span className="text-[16px] leading-[24px] text-[#5d5d5d]" data-testid="qrHelperText">
                    Mobil uygulamayı indirmek için QR kodu okut.
                  </span>
                </div>

                <div className="relative mr-[20px] w-fit">
                  <div
                    className='absolute left-[20px] top-[-7px] z-0 h-[181px] w-[2px]
                  bg-[#d5dde3] before:absolute before:bottom-0 before:left-[-5px] before:h-[12px] 
                  before:w-[12px] before:rounded-full before:bg-[#d5dde3]
                  before:content-[""] after:absolute after:right-[-5px] after:top-0 after:h-[12px] 
                  after:w-[12px] after:rounded-full after:bg-[#d5dde3] after:content-[""]
                  '
                  ></div>
                  <ul className="z-1 my-4 flex flex-col gap-2">
                    <li className="relative flex items-center gap-4" data-testid="qrStep1">
                      <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                        <HandphoneLock width={24} height={24} />
                      </span>
                      <p className="text-[14px] leading-[24px] text-[#2b3a42]">
                        QR kodu okut, mobil uygulamayı indir.
                      </p>
                    </li>

                    <li className="relative flex items-center gap-4" data-testid="qrStep2">
                      <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                        <ClipboardChecklist width={24} height={24} />
                      </span>
                      <p className="text-[14px] leading-[24px] text-[#2b3a42]">Uzaktan banka müşterisi ol.</p>
                    </li>

                    <li className="relative flex items-center gap-4" data-testid="qrStep3">
                      <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                        <GoldMedal width={24} height={24} />
                      </span>
                      <p className="text-[14px] leading-[24px] text-[#2b3a42]">
                        Bankaya özel avantajlardan faydalan!
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            )
          }

          {item.isConditionActive && (
            <div
              data-testid="conditions"
              className="accordion_content mt-6 max-h-[264px] w-full overflow-auto overscroll-contain rounded-[8px] bg-[#f6f6f6] p-3 md:mt-10"
              dangerouslySetInnerHTML={{ __html: item.conditions ?? '' }}
            ></div>
          )}
        </>
      }
    </Modal>
  );
};

const NativeCardContent = ({
  item,
  handleClosePopup,
  handleOpenPopup,
  imageElement,
  isPopupOpen,
  hasIntegration,
  integrationType,
  rsParam,
  error,
  isLoadingIntegrationData,
  integrationData,
}: NativeCardContentProps) => {
  const { isMobile } = useContext(DeviceContext);

  //@ts-ignore
  if (item.type === 2) {
    return (
      <NCContainer item={item} isNativeCard>
        <ProgrammableNativeCard item={item} key={item.id} />
      </NCContainer>
    );
  }

  //@ts-ignore // Hotfix icin gerekli. API tarafina henuz eklenmedi.
  if (item.isConditionActive || item.isOfferPopUp) {
    return (
      <>
        <button
          onClick={handleOpenPopup}
          className={classNames('h-auto w-full shadow-faq')}
          data-testid="nativeCardButton"
        >
          {imageElement}
        </button>
        {isPopupOpen && <ConditionsModal handleClosePopup={handleClosePopup} isPopupOpen={isPopupOpen} item={item} />}
      </>
    );
  }

  if (hasIntegration && item.link && item.target) {
    return (
      <>
        <button
          onClick={handleOpenPopup}
          className={classNames('h-auto w-full shadow-faq')}
          data-testid="nativeCardButton"
        >
          {imageElement}
        </button>
        {isPopupOpen && (
          <NativeCardModal
            isPopupOpen={isPopupOpen}
            item={integrationData}
            isLoading={isLoadingIntegrationData}
            error={error}
            closePopup={handleClosePopup}
            integrationType={integrationType}
            rsParam={rsParam}
            data-testid="nativeCardModal"
          />
        )}
      </>
    );
  }

  if (item.link && item.target) {
    return (
      <RelLink href={item.link} target={item.target} className="h-auto w-full shadow-faq" data-testid="nativeCardLink">
        {imageElement}
      </RelLink>
    );
  }

  return imageElement;
};

export const NativeCard: React.FC<Props> = ({ item }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [integrationData, setIntegrationData] = useState<any>(null);
  const [isLoadingIntegrationData, setIsLoadingIntegrationData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { isMobile } = useContext(DeviceContext);
  const searchParams = useSearchParams();

  const rsParam = searchParams?.get('rs');

  const nativeCardPictures = item.nativeCardPictures;

  const defaultImageSrc = '';
  const imageSrc = isMobile
    ? (nativeCardPictures?.mobileImagePath ?? nativeCardPictures?.tabletImagePath ?? defaultImageSrc)
    : (nativeCardPictures?.desktopImagePath ?? defaultImageSrc);

  const altTitle = nativeCardPictures?.altTitle ?? '';
  const imageAlt = `${altTitle} Kobi Kredisi`;

  const imageProps = {
    src: imageSrc,
    alt: imageAlt,
    width: 1120,
    height: 146,
    className: 'shadow-faq',
  };

  const integrationType = getIntegrationTypeFromLink(item.link ?? '');
  const hasIntegration = integrationType !== null;
  const showTooltip = Boolean(item.isActiveInformationTooltip);

  const imageElement = (
    <ImageWithFallback
      {...imageProps}
      loading="eager"
      priority
      decoding="async"
      style={{ objectFit: 'contain' }}
      data-testid="nativeCardImage"
    />
  );

  const handleOpenPopup = useCallback(async () => {
    const dataLayer = (window && 'dataLayer' in window && (window.dataLayer as any[])) || null;
    dataLayer?.push({
      event: 'GAEvent',
      eventCategory: 'Ticari Krediler - View',
      eventAction: 'Pop Up',
      eventLabel: `${item.name}`,
    });

    setIsPopupOpen(true);

    if (hasIntegration && item.link) {
      const idMatch = RegExp(/\d+/).exec(item.link);
      const integrationId = idMatch ? Number(idMatch[0]) : null;

      if (integrationId !== null && integrationId >= 0) {
        setIsLoadingIntegrationData(true);
        setError(null);
        try {
          const data = await BlogModulesActions(integrationId);
          setIntegrationData(data);
        } catch (err) {
          console.error('Error fetching integration data:', err);
          setError('Failed to load integration data.');
        } finally {
          setIsLoadingIntegrationData(false);
        }
      } else {
        console.warn('Invalid integration ID');
        setError('Invalid integration ID.');
        setIsLoadingIntegrationData(false);
      }
    }
  }, [hasIntegration, item.link]);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
    setIntegrationData(null);
    setError(null);
  }, []);

  // Automatically open popup if rsParam is '1' and integrationType matches
  // this 'rs' param is for dijikolay only, this should be refactored to support
  // future integrations with other brands
  useEffect(() => {
    if (rsParam === '1' && hasIntegration) {
      setIsPopupOpen(true);
    }
  }, [rsParam, hasIntegration]);

  return (
    <>
      <NativeCardContent
        item={item}
        handleClosePopup={handleClosePopup}
        handleOpenPopup={handleOpenPopup}
        isPopupOpen={isPopupOpen}
        imageElement={imageElement}
        error={error}
        hasIntegration={hasIntegration}
        integrationData={integrationData}
        integrationType={integrationType}
        isLoadingIntegrationData={isLoadingIntegrationData}
        rsParam={rsParam}
      />
      {showTooltip && (
        <NativeCardTooltip
          isActiveInfoTooltip={item.isActiveInformationTooltip ?? false}
          TooltipText={item.informationTooltipText ?? ''}
          isDesktop={!isMobile}
          tooltipTitle={item.informationTooltipText ?? ''}
          TooltipLeftToRight={item.informationTooltipLeftToRight ?? 0}
          TooltipTopToDown={item.informationTooltipTopToDown ?? 0}
        />
      )}
    </>
  );
};

export default NativeCard;
